import { Entity } from '@inter-ikea-core/core';
import {
  StoreAvailabilityProbabilityEnum,
  ThemeFontStyleTypeEnum
} from '@inter-ikea-kompis/enums/lib';
import { PlannerProductComponent, Product } from '@inter-ikea-storageone/range';
import { useAvailability } from 'contexts/availability';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { showInfoMessage } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Body from 'ui/Typography/Body';
import GetHomeDeliveryStockIndicator from '../HomeDeliveryAvailability/HomeDeliveryAvailability';
import GetInStoreStockIndicator from '../StoreAvailability/StoreAvailability';

type Props = {
  zipCode: string | null;
  storeId: string | null;
  product: Product;
  cluster?: Product[];
};

const ProductAvailabilty = styled(Body)`
  display: flex;
`;

export const AvailabilityStatusText = () => {
  const { formatMessage } = useIntl();
  return (
    <ProductAvailabilty
      type={ThemeFontStyleTypeEnum.bodySmall}
      data-testid="availability-status-text"
    >
      {formatMessage({ id: 'storageone.viewAvailabilityStatusText' })}
    </ProductAvailabilty>
  );
};

const Availability = ({ zipCode, storeId, product, cluster }: Props) => {
  const coreApi = useCore();
  const allProducts = useSelector(getMarketProducts);
  const dispatch = useAppDispatch();
  const { getProductStoreAvailability, getProductHomeDelivery } = useAvailability();

  useEffect(() => {
    const onInScene = (entity: Entity) => {
      if (
        entity &&
        entity.hasComponent(PlannerProductComponent.id) &&
        (storeId || zipCode !== undefined)
      ) {
        const entityArticleNbr = coreApi.entities.getGlobalArticleNumber(entity);

        if (!entityArticleNbr || !cluster?.map(p => p.globalItemNo).includes(entityArticleNbr)) {
          return;
        }
        const product = allProducts.find((p: any) => p.globalItemNo === entityArticleNbr);

        if (product && cluster && cluster.length > 1) {
          if (
            storeId !== null &&
            !hasSameStoreAvailability(cluster) &&
            getProductStoreAvailability(product) === StoreAvailabilityProbabilityEnum.outOfStock
          ) {
            if (
              zipCode !== undefined &&
              !hasSameHomeDeliveryAvailability(cluster) &&
              getProductHomeDelivery(product) === false
            ) {
              dispatch(
                showInfoMessage({
                  message: 'storageone.productListMessageUnavailableDeliveryAndStock'
                })
              );
            } else {
              dispatch(showInfoMessage({ message: 'storageone.productListMessageOutOfStock' }));
            }
          } else if (
            zipCode !== undefined &&
            !hasSameHomeDeliveryAvailability(cluster) &&
            getProductHomeDelivery(product) === false
          ) {
            dispatch(
              showInfoMessage({ message: 'storageone.productListMessageUnavailableDelivery' })
            );
          }
        }
      }
    };

    return coreApi.subscribe('inScene', onInScene);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreApi, dispatch, allProducts, storeId, zipCode]);

  const hasSameStoreAvailability = (products: Product[]) => {
    return products.length === 1
      ? true
      : products.every(
          (val, _, arr) => getProductStoreAvailability(val) === getProductStoreAvailability(arr[0])
        );
  };

  const hasSameHomeDeliveryAvailability = (products: Product[]) => {
    return products.length === 1
      ? true
      : products.every(
          (val, _, arr) => getProductHomeDelivery(val) === getProductHomeDelivery(arr[0])
        );
  };

  const nonComplimentaryProductParts = product?.parts
    ?.filter(part => {
      return part.complimentary === false;
    })
    ?.map(part => {
      return allProducts.find(p => p.globalItemNo === `${part.itemType}-${part.id}`);
    })
    .filter((p): p is Product => !!p) as Product[];

  const products: Product[] = cluster || nonComplimentaryProductParts;
  if (products.length === 0) {
    return null;
  }
  const sameHomeAvailability = hasSameHomeDeliveryAvailability(products);
  const sameStoreAvailability = hasSameStoreAvailability(products);
  const showAvailabilityStatusText = !sameHomeAvailability || !sameStoreAvailability;

  return (
    <>
      {!showAvailabilityStatusText ? (
        <>
          {zipCode && (
            <ProductAvailabilty type={ThemeFontStyleTypeEnum.bodySmall}>
              <GetHomeDeliveryStockIndicator product={products[0]} zipCode={zipCode} shortText />
            </ProductAvailabilty>
          )}
          {storeId && (
            <ProductAvailabilty type={ThemeFontStyleTypeEnum.bodySmall}>
              <GetInStoreStockIndicator product={products[0]} />
            </ProductAvailabilty>
          )}
        </>
      ) : (
        <AvailabilityStatusText />
      )}
    </>
  );
};

export default Availability;
